import { useState } from 'react';
import { useMutation, useQuery } from 'urql';
import PageHeader from '@/components/common/PageHeader.jsx';
import Field from '@/components/common/Field.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import { LIST_SLA, ACTIVAR_SLA } from './gql.js';

const TIPOS = [
  { value: 'URBANO', label: 'Urbano' },
  { value: 'ZONAS', label: 'Zonas' },
  { value: 'NACIONAL', label: 'Nacional' },
  { value: 'EXPRESS', label: 'Express' },
  { value: 'SAME_DAY', label: 'Same day' },
];

export default function SlaList({ onCreate, onSelect }) {
  const [filters, setFilters] = useState({ tipoServicio: '', zona: '' });
  const [pagination, setPagination] = useState({ limit: 20, offset: 0 });
  const [result] = useQuery({
    query: LIST_SLA,
    variables: {
      pagination,
      filters: {
        tipoServicio: filters.tipoServicio || null,
        zona: filters.zona || null,
      },
    },
  });
  const [, activarSla] = useMutation(ACTIVAR_SLA);

  const items = result.data?.listSLA.items ?? [];
  const pageInfo = result.data?.listSLA.pageInfo ?? pagination;

  const toggleActivo = async (sla) => {
    const resultToggle = await activarSla({ id: sla.id, activo: !sla.activo });
    if (resultToggle.error) {
      alert(resultToggle.error.message);
    }
  };

  return (
    <div className="w-full">
      <PageHeader
        title="Definiciones SLA"
        subtitle="Gestioná promesas de servicio y tiempos objetivo"
        actions={(
          <button
            type="button"
            onClick={onCreate}
            className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Nuevo SLA
          </button>
        )}
      />

      <section className="mx-auto mt-6 w-full max-w-[1400px] rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.98)] p-6 shadow-xl">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 sm:col-span-6">
            <Field label="Tipo de servicio" htmlFor="tipoServicio">
              <select
                id="tipoServicio"
                value={filters.tipoServicio}
                onChange={event => {
                  setFilters(prev => ({ ...prev, tipoServicio: event.target.value }));
                  setPagination(prev => ({ ...prev, offset: 0 }));
                }}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              >
                <option value="">Todos</option>
                {TIPOS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12 sm:col-span-6">
            <Field label="Zona" htmlFor="zona">
              <input
                id="zona"
                value={filters.zona}
                onChange={event => {
                  setFilters(prev => ({ ...prev, zona: event.target.value }));
                  setPagination(prev => ({ ...prev, offset: 0 }));
                }}
                placeholder="Ej: AMBA"
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full table-auto border-separate border-spacing-y-2 text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[rgba(var(--muted),0.9)]">
                <th className="px-3 py-2">Nombre</th>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2">Zona</th>
                <th className="px-3 py-2">Promesa (h)</th>
                <th className="px-3 py-2">1ª respuesta (min)</th>
                <th className="px-3 py-2">Activo</th>
              </tr>
            </thead>
            <tbody>
              {result.fetching ? (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-[rgba(var(--muted),0.9)]">
                    Cargando definiciones...
                  </td>
                </tr>
              ) : null}
              {!result.fetching && items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-6">
                    <EmptyState title="Sin registros" description="Creá una promesa para comenzar a medir los reclamos." />
                  </td>
                </tr>
              ) : null}
              {items.map(item => (
                <tr
                  key={item.id}
                  className="cursor-pointer rounded-xl bg-[rgba(var(--subpanel),0.7)] text-[rgb(var(--fg))] shadow transition hover:bg-[rgba(var(--subpanel),0.9)]"
                  onClick={() => onSelect?.(item)}
                >
                  <td className="px-3 py-3 font-semibold">{item.nombre}</td>
                  <td className="px-3 py-3">{item.tipoServicio}</td>
                  <td className="px-3 py-3">{item.zona || '—'}</td>
                  <td className="px-3 py-3">{item.promesaHoras}</td>
                  <td className="px-3 py-3">{item.primeraRespuestaMin}</td>
                  <td className="px-3 py-3">
                    <button
                      type="button"
                      onClick={event => {
                        event.stopPropagation();
                        toggleActivo(item);
                      }}
                      className={`rounded-full px-3 py-1 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${item.activo ? 'bg-emerald-500/20 text-emerald-500' : 'bg-slate-500/20 text-slate-500'}`}
                    >
                      {item.activo ? 'Activo' : 'Inactivo'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <StickyActions>
        <div className="flex w-full items-center justify-between text-xs text-[rgba(var(--muted),0.9)]">
          <span>
            {items.length} de {pageInfo.total} SLA
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(prev.offset - prev.limit, 0) }))}
              disabled={pageInfo.offset <= 0}
              className="rounded-xl border border-[rgba(var(--border),0.6)] px-3 py-1 text-sm text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.6)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
              disabled={pageInfo.offset + pageInfo.limit >= pageInfo.total}
              className="rounded-xl border border-[rgba(var(--border),0.6)] px-3 py-1 text-sm text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.6)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              Siguiente
            </button>
          </div>
        </div>
      </StickyActions>
    </div>
  );
}
